import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment/moment';
import Title from '../../../../commonComponents/Title';
import translate from '../../../../commonComponents/translate';
import ContentContainer from '../../../../commonComponents/pageLayout/ContentContainer';
import CareerFilters from './CareerFilters';
import GlobalSummary from './GlobalSummary';
import SubHeader from '../../../../commonComponents/pageLayout/SubHeader';
import Notifications from '../../../../services/notifications';
import Loader from '../../../../services/loader';
import MonthlySummary from './MonthlySummary';
import TopicBanner from '../../../../commonComponents/pageLayout/header/TopicBanner';
import { useModal } from '../../../../services/customHooks';
import MonthlyDetailsModal from './modals/MonthlyDetailsModal';
import LevelObjectives from './LevelObjectives';
import InvestmentDetailsModal from './modals/InvestmentDetailsModal';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import { getStartDateAndEndDateInterval } from '../../../../services/dateUtilityFunctions';

function MyCareer(props) {
  const { t, _csrf } = props;

  const loader = useRef(new Loader('#arcova-my-career'));
  const [isLoading, setLoading] = useState(false);

  const [monthlyDetailModal, setMonthlyDetailModal] = useModal({
    show: false,
    data: null,
  });

  const [investmentDetailModal, setInvestmentDetailModal] = useModal({
    show: false,
    data: null,
  });

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const [globalSummaryData, setGlobalSummaryData] = useState({
    isActive: false,
    activeUntilDate: Date.now(),
    totalAmountInvested: 0,
    nextAmountInvestmentsLevel: 0,
    nextInvestmentsLevelName: '',
    weeklyCapping: 0,
    currencyInfo: {},
  });

  const [monthlySummaryData, setMonthlySummaryData] = useState({
    VALeft: 0,
    VARight: 0,
    groupVA: 0,
    globalCommissions: 0,
    commissions: 0,
    remainingVA: 0,
    currencyInfo: {},
  });

  const [levelObjectivesData, setLevelObjectivesData] = useState([]);

  const { organization } = useContext(ConfigsContext);
  const { directSelling } = organization;

  // List — initial range in the plan timezone (BRAVON-6325), matching the
  // date-picker presets.
  const [initialStartDate, initialEndDate] =
    getStartDateAndEndDateInterval(directSelling);
  const startOfMonth = initialStartDate.valueOf();
  const today = initialEndDate.valueOf();

  const [filters, setFilters] = useState({
    user: null,
    startDate: startOfMonth,
    endDate: today,
  });

  const handleFiltersChange = useCallback(
    field => value =>
      setFilters(prevState => ({ ...prevState, [field]: value })),
    [setFilters],
  );
  const handleDateChange = (startDate, endDate) => {
    setFilters(prevState => ({
      ...prevState,
      startDate,
      endDate,
    }));
  };

  const handleGetGlobalSummary = useCallback(() => {
    axios
      .get('/api/tailor-made/arcova/summary/global', {
        params: filters,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setGlobalSummaryData(response.data);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters]);

  const handleGetMonthlySummary = useCallback(() => {
    axios
      .get('/api/tailor-made/arcova/summary/month', {
        params: filters,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setMonthlySummaryData(response.data);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
        Notifications.showErrorFromRequest(
          err,
          'error_fetchMonthlySummaryData',
        );
      });
  }, [filters]);

  const handleGetLevelObjectives = useCallback(() => {
    axios
      .get('/api/tailor-made/arcova/level/objectives', {
        params: filters,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setLevelObjectivesData(response.data);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters.user]);

  useEffect(() => {
    handleGetGlobalSummary();
    handleGetMonthlySummary();
  }, [filters]);
  useEffect(() => {
    handleGetLevelObjectives();
  }, [filters.user]);

  const showMonthlySummaryModal = useCallback(() => {
    axios
      .get('/api/tailor-made/arcova/reports/list', {
        params: filters,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setMonthlyDetailModal({ show: true, data: response.data });
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchReportsList');
      });
  }, [filters, setMonthlyDetailModal]);

  const showInvestmentDetailsModal = useCallback(() => {
    axios
      .get('/api/tailor-made/arcova/investments/list', {
        params: filters,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setInvestmentDetailModal({ show: true, data: response.data });
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchInvestmentsList');
      });
  }, [filters, setInvestmentDetailModal]);

  return (
    <>
      {investmentDetailModal.show ? (
        <InvestmentDetailsModal
          filters={filters}
          onClose={() => setInvestmentDetailModal({ show: false, data: null })}
        />
      ) : null}
      {monthlyDetailModal.show ? (
        <MonthlyDetailsModal
          filters={filters}
          onClose={() => setMonthlyDetailModal({ show: false, data: null })}
        />
      ) : null}
      <Title title={t('header_myCareer')}>
        <ContentContainer>
          <SubHeader title={t('header_myCareer')} />
          <TopicBanner />
          <div className="m-content" id="arcova-my-career">
            <div className="full-page-container-with-subheader">
              <CareerFilters
                t={t}
                onDateChange={handleDateChange}
                onFilterChange={handleFiltersChange}
                filters={filters}
              />
              <div className="career-summaries">
                <GlobalSummary
                  t={t}
                  data={globalSummaryData}
                  showModal={showInvestmentDetailsModal}
                />
                <MonthlySummary
                  t={t}
                  data={monthlySummaryData}
                  showModal={showMonthlySummaryModal}
                />
              </div>
              <LevelObjectives t={t} data={levelObjectivesData} />
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

MyCareer.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
};

export default translate(MyCareer);
